<template>
	<section class="plan">
		<div v-if="overviewList.length" class="plan-amounts">
			<div v-for="(item, index) in overviewList" :key="index" class="plan-amount">
				<div class="plan-price">{{ item.amount }}</div>
				<div class="plan-label">{{ item.label }}</div>
			</div>
		</div>

		<div v-if="formatDetails.length" class="plan-details">
			<!-- 普通明细信息 -->
			<div v-for="(item, index) in visibleDetails" :key="item.name + index">
				<div class="plan-detail">
					<div class="plan-name">
						<span>{{ item.name }}</span>
						<span v-if="item.period" class="plan-period">{{ item.period }}</span>
					</div>
					<div class="plan-value">
						<s v-if="formatValue(item, item.discount || item.src_val)">
							{{ formatValue(item, item.discount || item.src_val) }}
						</s>
						<span>{{ formatValue(item, item.value) }}</span>
					</div>
				</div>

				<!-- 期限明细下展示月供明细 -->
				<div
					v-if="item.name === '期限' && formatInstallmentDetails.length"
					:key="'installments-' + index"
					class="installment-details"
				>
					<div
						v-for="(monthlyItem, monthlyIndex) in formatInstallmentDetails"
						:key="monthlyItem.name + monthlyIndex"
						class="plan-detail"
					>
						<div class="plan-name">
							<span>{{ monthlyItem.name }}</span>
							<span v-if="monthlyItem.period" class="plan-period">{{ monthlyItem.period }}</span>
						</div>
						<div class="plan-value installment-value">
							<span>{{ formatValue(monthlyItem, monthlyItem.value) }}</span>
							<s v-if="formatValue(monthlyItem, monthlyItem.src_val)">
								{{ formatValue(monthlyItem, monthlyItem.src_val) }}
							</s>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-if="shouldFold" class="plan-expand" @click="expanded = !expanded">
			{{ expanded ? '收起' : '展开全部' }}
			<nio-icon :name="expanded ? 'icon_arrows_top_2xs' : 'icon_arrows_down_2xs'" />
		</div>

		<!-- 分段明细 -->
		<div
			v-for="(period, index) in formatPeriodDetails"
			:key="'period-' + index"
			class="period-details"
		>
			<div class="period-title">{{ period.title }}</div>
			<div
				v-for="(item, itemIndex) in period.values"
				:key="item.name + itemIndex"
				class="plan-detail"
			>
				<div class="plan-name">{{ item.name }}</div>
				<div class="plan-value">
					<s v-if="formatValue(item, item.src_val)">{{ formatValue(item, item.src_val) }}</s>
					<span>{{ formatValue(item, item.value) }}</span>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
export default {
	name: 'FinanceRecommendPlan',
	props: {
		overviewList: {
			type: Array,
			default: () => []
		},
		// 明细信息
		details: {
			type: Array,
			default: () => []
		},
		// 分段的明细信息 1-11期，11-21期，依此类推
		periodDetailInfos: {
			type: Array,
			default: () => []
		},
		// 详细月供明细信息
		monthlyInstallmentInfos: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return { expanded: false }
	},
	computed: {
		// 普通明细信息
		formatDetails() {
			return this.format(this.details)
		},
		// 月供明细信息
		formatInstallmentDetails() {
			return this.format(this.monthlyInstallmentInfos)
		},
		// 分段明细信息
		formatPeriodDetails() {
			return this.periodDetailInfos.map(period => ({
				title: period.title,
				values: this.format(period.values || [])
			}))
		},
		// 过滤普通明细信息中包含月供的条目
		monthPaymentItems() {
			return this.formatDetails.filter(item => item.name.indexOf('月供') !== -1)
		},
		// 普通明细中包含的月供明细大于两条时需要折叠
		shouldFold() {
			return this.monthPaymentItems.length > 2
		},
		// 可见的普通明细信息，根据折叠规则决定显示哪些条目
		visibleDetails() {
			if (!this.shouldFold || this.expanded) return this.formatDetails
			const firstTwoMonthPayments = this.monthPaymentItems.slice(0, 2)
			// 仅显示前两条月供明细，其余折叠
			return this.formatDetails.filter(
				item => item.name.indexOf('月供') === -1 || firstTwoMonthPayments.indexOf(item) !== -1
			)
		}
	},
	methods: {
		// 格式化列表项，保留有效值
		format(list) {
			return list
				.map(item => ({
					...item,
					value: item.dst_val || item.value
				}))
				.filter(item => this.formatValue(item, item.value))
		},
		formatInteger(value) {
			if (value === undefined || value === null || value === '') return '--'
			const number = Number(value)
			return Number.isNaN(number) ? String(value) : Math.round(number).toLocaleString('en-US')
		},
		formatValue(item, value) {
			if (value === undefined || value === null || value === '') return ''

			switch (item.code) {
				case 'month':
					return `${value}期`
				case 'money':
				case 'price':
				case 'amount':
					const number = Number(value)
					return !Number.isNaN(number) && number !== 0 ? '¥' + this.formatInteger(number) : ''
				case 'ratio':
				case 'annualized_rate':
				case 'interest_rate':
					return value ? `${(parseFloat(value) * 100).toFixed(2)}%` : ''
				default:
					return value
			}
		}
	}
}
</script>

<style scoped lang="scss">
@import '~@nio-fe/nio-app-ui/es/style';

.plan {
	padding: 20px 20px 5px;
	background: var(--bg_secondary);
	margin-top: 10px;
}

.plan-amounts {
	display: flex;
	padding-bottom: 16px;
	border-bottom: 1px solid var(--border_divider_primary);
}

.plan-amount {
	flex: 1;
	min-width: 0;
	text-align: center;

	&:nth-child(2) {
		padding-left: 12px;
	}
}

.plan-price {
	@include subtitle_2(1.2, 700);
	white-space: nowrap;
}

.plan-prefix {
	margin-right: 4px;
	font-size: 12px;
}

.plan-label,
.plan-name {
	color: var(--text_secondary);
}

.plan-label {
	margin-top: 4px;
}

.plan-details {
	padding-top: 15px;
}

.plan-detail {
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	@include body_2(1.5, 400, false);
	margin-bottom: 15px;

	> :first-child {
		margin-right: 12px;
	}
}

.plan-name {
	display: flex;
	align-items: baseline;

	> :first-child {
		margin-right: 8px;
	}
}

.plan-period {
	color: var(--text_tertiary);
}

.plan-value {
	color: var(--text-icon-primary);
	text-align: right;
	white-space: nowrap;

	s {
		margin-right: 8px;
		@include body_3(1.5, 400, false);
		color: var(--text_tertiary);
	}
}

.plan-expand {
	margin: 8px auto 0;
	color: var(--text_secondary);
	text-align: center;
	@include body_3(1.5, 400, false);
}

.installment-details,
.period-details {
	padding-top: 15px;
	border-top: 1px solid var(--border_divider_primary);
}

.installment-details {
	margin-bottom: 15px;
	border-bottom: 1px solid var(--border_divider_primary);
}

.installment-value s {
	margin-right: 0;
	margin-left: 8px;
}

.period-details {
	margin-top: 15px;
}

.period-title {
	margin-bottom: 15px;
	@include body_2(1.5, 500, false);
}
</style>
